'use client'

import { useCallback, useEffect, useRef } from 'react'
import { useReportWebVitals } from 'next/web-vitals'
import posthog from 'posthog-js'
import { usePathname } from 'next/navigation'

/**
 * Real-User Monitoring (RUM) reporter — captures Core Web Vitals for every
 * user on every route and streams them to PostHog via the `$web_vitals`
 * event. Pairs with the lab-only Lighthouse harness at
 * `scripts/perf-baseline.ts` so we finally have ground-truth perf data.
 *
 * Metrics captured (from `next/web-vitals`, which wraps google/web-vitals):
 *   LCP  — Largest Contentful Paint (good: < 2500 ms)
 *   FCP  — First Contentful Paint   (good: < 1800 ms)
 *   INP  — Interaction to Next Paint (good: < 200 ms)
 *   CLS  — Cumulative Layout Shift  (good: < 0.1)
 *   TTFB — Time to First Byte       (good: < 800 ms)
 *   FID  — First Input Delay (legacy, superseded by INP on most browsers)
 *   Next.js-specific: Next.js-hydration, Next.js-route-change-to-render, Next.js-render
 *
 * PostHog shape: `$web_vitals` custom event with {metric_name, value, rating,
 * delta, navigation_type, pathname, route_template}. The route_template
 * normalizes UUIDs in the URL so dashboards can group by route shape
 * (e.g. `/in/[org]/p/[id]`) instead of unique post IDs.
 *
 * Mounted once in the root layout (see `app/layout.tsx`). Returns null —
 * no DOM. Zero new npm deps: `next/web-vitals` ships with Next.js 16.
 */

// Matches any UUID segment in the pathname. Used to collapse `/in/horizon/p/<uuid>`
// → `/in/[org]/p/[id]` for dashboard grouping. The /in/ org slug is replaced
// separately because it isn't a UUID but still varies per request.
const UUID_SEGMENT = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g

function normalizeRoute(pathname: string): string {
  const withoutUuid = pathname.replace(UUID_SEGMENT, '/[id]')
  // /in/<orgSlug>/... → /in/[org]/...
  return withoutUuid.replace(/^\/in\/[^/]+/, '/in/[org]')
}

export function WebVitalsReporter() {
  const pathname = usePathname()

  // Next.js `useReportWebVitals` re-registers its internal listener whenever
  // the callback identity changes. Inlining `(metric) => { ... pathname ... }`
  // would re-register on every route change (and potentially emit duplicate
  // reports for metrics the underlying web-vitals library replays). Keep the
  // callback stable via `useCallback([])` and read the current pathname
  // through a ref that is synchronized in an effect (React 19 disallows
  // mutating refs during render).
  const pathnameRef = useRef(pathname)
  useEffect(() => {
    pathnameRef.current = pathname
  }, [pathname])

  const report: Parameters<typeof useReportWebVitals>[0] = useCallback((metric) => {
    // Guard against capture before PostHog is initialized — otherwise the
    // call no-ops silently but still constructs the event object.
    if (typeof window === 'undefined' || !posthog.__loaded) return

    const currentPath = pathnameRef.current
    posthog.capture('$web_vitals', {
      metric_name: metric.name,
      value: metric.value,
      rating: 'rating' in metric ? metric.rating : undefined,
      delta: metric.delta,
      metric_id: metric.id,
      navigation_type: 'navigationType' in metric ? metric.navigationType : undefined,
      pathname: currentPath,
      route_template: normalizeRoute(currentPath),
    })
  }, [])

  useReportWebVitals(report)

  return null
}
